import InputBase from '@mui/material/InputBase';
import type {InputBaseProps} from '@mui/material/InputBase';
import {useId} from 'react';
import type {ReactNode} from 'react';

import css from './Input.module.css';

export interface InputProps extends Omit<InputBaseProps, 'error' | 'size' | 'classes'> {
	label?: ReactNode;
	required?: boolean;
	error?: boolean;
	/** Текст под полем. Совмещает ошибку и обычную подсказку — как в otveto. */
	helperText?: ReactNode;
	size?: 'small' | 'medium';
	/** Пометка справа от названия поля: «авто», «изменено» и подобное. */
	labelAdornment?: ReactNode;
	/** Счётчик символов справа под полем. Заодно ставит maxLength. */
	maxCount?: number;
	currentCount?: number;
	endAdornmentNode?: ReactNode;
	/**
	 * Пробельные символы не попадают в значение: срезаются при вводе и вставке.
	 * Для полей, где пробел — всегда опечатка (пароль, логин), а не формат
	 * (ФИО, адрес) — подсветкой ошибки тут не обойтись, её пришлось бы
	 * показывать уже после того, как пробел введён.
	 */
	noSpaces?: boolean;
}

/**
 * Базовое поле кита ОТВЕТО: внешний лейбл сверху, рамка на переменных --input-*,
 * подсказка и счётчик символов под полем.
 *
 * Ошибку показываем только после touched — это делает вызывающий код,
 * см. src/components/form/formikField.ts.
 */
export function Input({
	label,
	required,
	error,
	helperText,
	size = 'medium',
	labelAdornment,
	maxCount,
	currentCount,
	endAdornmentNode,
	noSpaces,
	disabled,
	multiline,
	inputProps,
	className,
	onChange,
	...rest
}: InputProps) {
	/*
	 * Текст под полем скринридер объявляет только через aria-describedby: соседний
	 * с полем <p> для assistive tech невидим, и поле с ошибкой оставалось бы
	 * «невалидным» без причины. Ссылка склеивается с внешней, если поле описывают
	 * ещё и своим текстом (текст ошибок строки прайса, SKS-580).
	 */
	const helperId = useId();
	const describedBy = [inputProps?.['aria-describedby'], helperText ? helperId : undefined]
		.filter(Boolean)
		.join(' ');

	/*
	 * noSpaces применяется до вызывающего onChange: значение с обрезанными
	 * пробелами должно доехать и в состояние формы, иначе поле «прыгало» бы —
	 * контролируемый input перерисовывал бы введённый пробел обратно.
	 */
	const handleChange: InputBaseProps['onChange'] = (event) => {
		if (noSpaces && /\s/.test(event.target.value)) {
			event.target.value = event.target.value.replace(/\s+/g, '');
		}

		onChange?.(event);
	};

	// className достаётся из props и подмешивается, а не спредится: className={fieldClasses}
	// стоит после {...rest} и молча затирал переданный снаружи класс
	const fieldClasses = [
		css.field,
		css[size],
		error ? css.error : '',
		disabled ? css.disabled : '',
		className ?? '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className={css.wrapper}>
			{label ? (
				<span className={`${css.label} ${disabled ? css.labelDisabled : ''}`}>
					{label}
					{required ? <span className={css.required}>*</span> : null}
					{labelAdornment ? <span className={css.labelAdornment}>{labelAdornment}</span> : null}
				</span>
			) : null}

			<InputBase
				{...rest}
				disabled={disabled}
				multiline={multiline}
				fullWidth
				className={fieldClasses}
				onChange={handleChange}
				classes={{
					focused: css.focused,
					input: multiline ? `${css.input} ${css.multiline}` : css.input,
				}}
				inputProps={{
					...inputProps,
					maxLength: maxCount ?? inputProps?.maxLength,
					...(describedBy ? {'aria-describedby': describedBy} : {}),
					// error в ките красит рамку; для скринридера невалидность объявляет
					// только этот атрибут, InputBase его сам не ставит
					...(error ? {'aria-invalid': true} : {}),
				}}
				endAdornment={
					endAdornmentNode ? (
						<span className={css.endAdornment}>{endAdornmentNode}</span>
					) : undefined
				}
			/>

			{helperText || maxCount ? (
				<div className={css.helperRow}>
					<p
						id={helperText ? helperId : undefined}
						className={`${css.helper} ${error ? css.helperError : ''}`}
					>
						{helperText}
					</p>
					{maxCount ? (
						<span className={css.counter}>
							{currentCount ?? 0}/{maxCount}
						</span>
					) : null}
				</div>
			) : null}
		</div>
	);
}
